/* eslint-env browser */
// A list page keeps its filters when staff open a record and come back
// (owner, 28 Oct 2026: "any child level navigation and clicking back to the
// parent should never reset the filter"). The values live in this browser
// tab only (sessionStorage) and per signed-in person: a new tab, a closed
// tab or another account starts clean. JSON values only (dates as epoch ms).
//
//   const [status, setStatus] = usePageState('bookings', 'status', 'all');
//
// Same as useState, plus: the first value is the one kept from the last visit
// when it has the same kind (string, number, boolean, array or object) as the
// default; anything else falls back to the default. A null `page` makes it a
// plain useState (DataTable without a stateKey).
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const storeKey = (page, name, uid) => `dd_page_${page}_${name}_${uid || 'anon'}`;

const kindOf = (v) => (Array.isArray(v) ? 'array' : v === null ? 'null' : typeof v);

export const readPageState = (page, name, uid, fallback) => {
  if (!page) return fallback;
  try {
    const raw = window.sessionStorage.getItem(storeKey(page, name, uid));
    if (raw === null) return fallback;
    const value = JSON.parse(raw);
    if (fallback !== null && fallback !== undefined && kindOf(value) !== kindOf(fallback)) return fallback;
    return value;
  } catch (e) {
    return fallback;
  }
};

export const writePageState = (page, name, uid, value) => {
  if (!page) return;
  try {
    if (value === undefined) window.sessionStorage.removeItem(storeKey(page, name, uid));
    else window.sessionStorage.setItem(storeKey(page, name, uid), JSON.stringify(value));
  } catch (e) {
    // Storage blocked or full: the filter is simply not kept.
  }
};

export function usePageState(page, name, initial) {
  const uid = useSelector((state) => (state.auth && state.auth.profile ? state.auth.profile.uid : null));
  const [value, setValue] = useState(() => {
    const fallback = typeof initial === 'function' ? initial() : initial;
    return readPageState(page, name, uid, fallback);
  });
  useEffect(() => {
    writePageState(page, name, uid, value);
  }, [page, name, uid, value]);
  return [value, setValue];
}

// Memory a page keeps next to its filters (BookingHistory's loaded time
// range) is forgotten with them.
const resetters = new Set();
export const onPageStateReset = (fn) => {
  resetters.add(fn);
  return () => resetters.delete(fn);
};

// Sign-out: every kept filter of this tab goes, so the next person on a
// shared computer starts clean.
export const resetPageStates = () => {
  try {
    const keys = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.indexOf('dd_page_') === 0) keys.push(k);
    }
    keys.forEach((k) => window.sessionStorage.removeItem(k));
  } catch (e) {
    // Storage blocked: nothing was kept.
  }
  resetters.forEach((fn) => {
    try { fn(); } catch (e) { /* a page's own reset failing must not stop the sign-out */ }
  });
};

// "Back" on a detail page: to the page that opened it in this tab - a
// booking opened from a driver's trips goes back there, not to Bookings -
// or to `to` when there is none (a link opened in a new tab). react-router
// numbers this tab's history entries in history.state.idx.
export const goBackOr = (navigate, to, options) => {
  let idx = 0;
  try {
    const st = window.history.state;
    idx = st && typeof st.idx === 'number' ? st.idx : 0;
  } catch (e) {
    idx = 0;
  }
  if (idx > 0) navigate(-1);
  else navigate(to, options);
};

export default usePageState;
